import { X } from "lucide-react";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { PAGE_VIEW_COOKIE_NAME, POPUP_SEEN_COOKIE_NAME } from "../popup-middleware";
import type { AllActivePopupsResult } from "../sanity-schema";
import { SanityImage } from "./SanityImage";
import { SanityText } from "./SanityText";

export const SanityPopup: React.FC<{
  popup: AllActivePopupsResult[number] | null | undefined;
}> = ({ popup }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    dialogRef.current?.close();
    setIsOpen(false);
    // biome-ignore lint/suspicious/noDocumentCookie: intentional cookie for popup tracking
    document.cookie = document.cookie
      .split(";")
      .filter((s) => s.trim().startsWith(POPUP_SEEN_COOKIE_NAME))
      .concat(`${POPUP_SEEN_COOKIE_NAME}=${Date.now()}`)
      .join(";");
  }, []);

  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = () => {
      const pageViews = parseInt(
        document.cookie
          .split(/;\s?/)
          .find((s) => s.trim().startsWith(PAGE_VIEW_COOKIE_NAME))
          ?.split("=")[1] ?? "0",
        10,
      );
      // biome-ignore lint/suspicious/noDocumentCookie: intentional cookie for page view tracking
      document.cookie = document.cookie
        .split(/;\s?/)
        .filter((s) => !s.trim().startsWith(PAGE_VIEW_COOKIE_NAME))
        .concat(`${PAGE_VIEW_COOKIE_NAME}=${pageViews + 1}`)
        .join("; ");
    };
    router?.events?.on("routeChangeStart", handleRouteChange);
    return () => {
      router?.events?.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    const t = setTimeout(
      () => {
        dialogRef.current?.showModal();
        setIsOpen(true);
      },
      (popup?.dwellTime ?? 0) * 1000,
    );
    return () => clearTimeout(t);
  }, [popup]);

  // Close on click outside (click on backdrop hits the <dialog> itself)
  const handleDialogClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  // Handle native close event (e.g. Escape key)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      if (isOpen) {
        handleClose();
      }
    };
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, [isOpen, handleClose]);

  if (!popup) {
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      className="w-full max-w-xl p-0 m-auto bg-transparent backdrop:bg-black/50 backdrop:backdrop-blur-sm open:animate-modal-in"
      onClick={handleDialogClick}
      onKeyDown={(e) => {
        if (e.key === "Escape") handleClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 flex items-center justify-center size-7 rounded-full bg-white cursor-pointer transition-colors hover:bg-gray-100"
        >
          <X className="size-4 text-gray-600" />
        </button>
        <SanityImage image={popup.image} className="rounded-t-xl h-[260px] object-cover w-full" />
        <div className="p-5">
          <SanityText content={popup.content} />
        </div>
        <div className="flex justify-center p-5 pt-0">
          <a
            href={popup.button?.link ?? "#"}
            className="block w-full text-center bg-brand-mint text-brand-black py-3 rounded-xl text-sm font-semibold cursor-pointer transition-all active:scale-[0.98]"
          >
            {popup.button?.text}
          </a>
        </div>
      </div>
    </dialog>
  );
};
